import ExcelJS from "exceljs";
import type { ParsedWorksheet } from "@/types";

/** Mirrors pandas' header-naming defaults so the preview matches what the backend
 * produces after upload: blank -> `Unnamed: {index}`, duplicates -> `.N` suffix. */
function synthesizeHeaders(rawHeaders: string[]): string[] {
  const named = rawHeaders.map((header, index) => header || `Unnamed: ${index}`);

  const counts = new Map<string, number>();
  const result: string[] = [];
  for (const name of named) {
    let col = name;
    let curCount = counts.get(col) ?? 0;
    while (curCount > 0) {
      counts.set(col, curCount + 1);
      const candidate = `${col}.${curCount}`;
      if (counts.has(candidate)) {
        curCount += 1;
      } else {
        col = candidate;
        curCount = counts.get(col) ?? 0;
      }
    }
    result.push(col);
    counts.set(col, (counts.get(col) ?? 0) + 1);
  }
  return result;
}

/** Parse an uploaded file into worksheets */
export async function parseFile(file: File): Promise<ParsedWorksheet[]> {
  const ext = file.name.split(".").pop()?.toLowerCase();

  switch (ext) {
    case "xlsx":
      return parseExcel(file);
    case "csv":
      return parseCSV(file);
    case "xls":
      throw new Error(
        "Legacy .xls format is not supported. Please convert your file to .xlsx " +
          '(File > Save As > "Excel Workbook (.xlsx)") and re-upload.'
      );
    case "json":
      return parseJSON(file);
    case "xml":
      return parseXML(file);
    default:
      throw new Error(`Unsupported file type: .${ext}`);
  }
}

async function parseExcel(file: File): Promise<ParsedWorksheet[]> {
  const buffer = await file.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);

  const worksheets: ParsedWorksheet[] = [];

  workbook.eachSheet((sheet) => {
    const rows: Record<string, unknown>[] = [];

    // First row is treated as headers
    const headerRow = sheet.getRow(1);
    const colCount = Math.max(headerRow.cellCount, sheet.columnCount);
    const rawHeaders: string[] = [];
    for (let colNumber = 1; colNumber <= colCount; colNumber++) {
      rawHeaders[colNumber - 1] = cellToString(headerRow.getCell(colNumber).value);
    }
    const headers = synthesizeHeaders(rawHeaders);

    // Remaining rows are data
    for (let rowIdx = 2; rowIdx <= sheet.rowCount; rowIdx++) {
      const row = sheet.getRow(rowIdx);
      // Skip entirely empty rows
      if (isRowEmpty(row, headers.length)) continue;

      const record: Record<string, unknown> = {};
      headers.forEach((header, colIdx) => {
        const cell = row.getCell(colIdx + 1);
        record[header] = cellToString(cell.value);
      });
      rows.push(record);
    }

    worksheets.push({
      name: sheet.name,
      headers,
      rows,
      totalRows: rows.length,
    });
  });

  return worksheets;
}

/**
 * Safely convert any ExcelJS cell value to a display string.
 * Handles: null, string, number, boolean, Date, RichText,
 * formula results, hyperlinks, and error values.
 */
function cellToString(value: ExcelJS.CellValue): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value instanceof Date) return value.toISOString().split("T")[0] ?? "";

  // Rich text — concatenate fragments
  if (typeof value === "object" && "richText" in value) {
    return (value as { richText: { text: string }[] }).richText.map((rt) => rt.text).join("");
  }

  // Formula — use the cached result
  if (typeof value === "object" && "result" in value) {
    return cellToString((value as { result: ExcelJS.CellValue }).result);
  }

  // Hyperlink
  if (typeof value === "object" && "hyperlink" in value) {
    return (value as { text?: string; hyperlink: string }).text ?? "";
  }

  // Error value
  if (typeof value === "object" && "error" in value) {
    return String((value as { error: unknown }).error);
  }

  return String(value);
}

/** Check if a worksheet row is entirely empty up to the header width */
function isRowEmpty(row: ExcelJS.Row, colCount: number): boolean {
  for (let c = 1; c <= colCount; c++) {
    const val = row.getCell(c).value;
    if (val !== null && val !== undefined && val !== "") return false;
  }
  return true;
}

async function parseJSON(file: File): Promise<ParsedWorksheet[]> {
  const text = await file.text();
  const data = JSON.parse(text);
  const rows = Array.isArray(data) ? data : [data];
  const headers = rows.length > 0 ? Object.keys(rows[0] as object) : [];

  return [
    {
      name: "Data",
      headers,
      rows: rows as Record<string, unknown>[],
      totalRows: rows.length,
    },
  ];
}

async function parseCSV(file: File): Promise<ParsedWorksheet[]> {
  const text = await file.text();
  const rows = parseCsvRows(text);

  if (rows.length === 0) {
    return [{ name: "csv", headers: [], rows: [], totalRows: 0 }];
  }

  const [rawHeaders = [], ...dataRows] = rows;
  const headers = synthesizeHeaders(rawHeaders);
  const parsedRows = dataRows
    .filter((row) => row.some((value) => value !== ""))
    .map((row) => {
      const record: Record<string, unknown> = {};
      headers.forEach((header, index) => {
        record[header] = row[index] ?? "";
      });
      return record;
    });

  return [
    {
      name: "csv",
      headers,
      rows: parsedRows,
      totalRows: parsedRows.length,
    },
  ];
}

function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const next = text[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        cell += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      row.push(cell);
      cell = "";
      continue;
    }

    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && next === "\n") {
        i += 1;
      }
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
      continue;
    }

    cell += char;
  }

  if (cell !== "" || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }

  return rows.filter((parsedRow) => !(parsedRow.length === 1 && parsedRow[0] === ""));
}

async function parseXML(file: File): Promise<ParsedWorksheet[]> {
  const text = await file.text();
  const parser = new DOMParser();
  const xml = parser.parseFromString(text, "text/xml");

  const parseError = xml.querySelector("parsererror");
  if (parseError) {
    throw new Error("Invalid XML file");
  }

  // Attempt to extract tabular data from XML
  const root = xml.documentElement;
  const children = Array.from(root.children);

  if (children.length === 0) {
    return [{ name: "Data", headers: [], rows: [], totalRows: 0 }];
  }

  // Assume each child element is a row, its child elements are columns
  const rows: Record<string, unknown>[] = [];
  const headerSet = new Set<string>();

  for (const child of children) {
    const row: Record<string, unknown> = {};
    for (const col of Array.from(child.children)) {
      headerSet.add(col.tagName);
      row[col.tagName] = col.textContent ?? "";
    }
    rows.push(row);
  }

  return [
    {
      name: root.tagName,
      headers: Array.from(headerSet),
      rows,
      totalRows: rows.length,
    },
  ];
}
